import { useEffect } from 'react';

import { useConfigContext } from '@omq/shared';

import type { HelpPage } from '../api/help';
import { URLStore } from '../stores/url-store';

const SCRIPT_ELEMENT_ID = 'omq-faq-structured-data';

/**
 * Escape a value so it can be safely interpolated into a JSON string literal
 * inside the JSON-LD snippet. Returns the escaped content WITHOUT the
 * surrounding quotes, since the placeholders already sit between quotes in the
 * server-rendered template. Using JSON.stringify guarantees correct escaping
 * of double quotes, backslashes and control characters.
 *
 * Like the server-side template, the HTML-significant characters <, > and &
 * are emitted as Unicode escapes (RFC 8259) so the whole block stays
 * script-safe without any HTML escaping — the SSR renderer serializes it into
 * plain HTML, and Google's JSON-LD extraction no longer unrolls double-escaped
 * HTML entities.
 *
 * @param value - raw value (e.g. localized text or url)
 * @returns JSON-escaped content without surrounding quotes
 */
function escapeForJsonString(value: string): string {
  return JSON.stringify(value)
    .slice(1, -1)
    .replace(/</g, '\\u003C')
    .replace(/>/g, '\\u003E')
    .replace(/&/g, '\\u0026');
}

/**
 * Add FAQ structured data to head of embedding page.
 * @see https://developers.google.com/search/docs/data-types/faqpage
 *
 * If passed helpPage has a faqSnippet, create/update script tag in page header.
 * Otherwise, remove script / do nothing.
 *
 * The breadcrumb root item's name ({title}) is always the account's localized
 * navigation_root text; all URLs in the snippet (breadcrumb root, category
 * items, and the canonical question leaf) are built around the {baseUrl}
 * placeholder, resolved here from the embedding page.
 *
 * @param helpPage
 * @param enabled - when false, the hook is fully inert: it never creates,
 *                  updates, or removes the structured data script tag
 */
export function useFaqStructuredData(
  helpPage: HelpPage | null | undefined,
  enabled: boolean = true,
): void {
  const config = useConfigContext();

  // Resolved outside the effect so the dependency is a stable string: the
  // effect re-runs only when the text itself changes.
  const rootName = config.loc('navigation_root');

  useEffect(() => {
    // structured data handling disabled via integration flag —
    // leave any existing (e.g. server-side prerendered) JSON-LD untouched
    if (!enabled) {
      return;
    }

    const faqSnippetElement = document.getElementById(SCRIPT_ELEMENT_ID);
    let faqSnippet = helpPage?.faqSnippet;

    // if there is no data
    // remove data from page
    if (faqSnippet == null) {
      if (faqSnippetElement != null) {
        faqSnippetElement.remove();
      }

      return;
    }

    // JSON-escape each replacement value so quotes/backslashes/control chars in
    // the text or url cannot break the JSON-LD block. Function replacers are
    // used so '$' sequences in the values are not treated as replacement
    // patterns by replaceAll.
    faqSnippet = faqSnippet
      // replace baseUrl placeholder with cleaned base url
      .replaceAll('{baseUrl}', () => escapeForJsonString(URLStore.getCleanBaseurl()))
      // title: the account's localized navigation_root text (matches the
      // visible breadcrumb root)
      .replaceAll('{title}', () => escapeForJsonString(rootName));

    // if structured data element does not exist
    // create & append to head
    if (faqSnippetElement == null) {
      // create script tag
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = SCRIPT_ELEMENT_ID;
      script.innerText = faqSnippet;

      // append script to head
      const head = document.querySelector('head');
      if (head != null) {
        head.appendChild(script);
      }
    } else {
      // update script tag
      faqSnippetElement.innerText = faqSnippet;
    }
  }, [helpPage?.faqSnippet, enabled, rootName]);
}
